import type { DineBillPrint } from 'api/types';

/**
 * DO-408 - the GST invoice on paper. An 80 mm receipt layout opened in its own
 * window and sent to the browser's print dialog, so any printer the till can
 * reach works. Numbers come from the API payload untouched: the paper shows the
 * snapshot, never a recomputation.
 */
const money = (n: number) => n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const esc = (s: string | undefined | null) => (s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string));

const customerLabel = (t: string) => (t === 'IN_HOUSE' ? 'In-house guest' : t === 'CORPORATE' ? 'Corporate' : 'Walk-in');

/**
 * The tax summary by SAC/HSN and rate (DineOps tax T8): one heading per SAC/HSN with its
 * taxable value, then its components. Bills before the outlet tax profile have no SAC: their
 * rows print as before.
 */
export function taxSummaryRows(taxes: DineBillPrint['bill']['taxes']): string {
  const groups = new Map<string, typeof taxes>();
  for (const t of taxes) {
    const key = `${t.sacHsnCode ?? ''}|${t.taxGroupCode}`;
    groups.set(key, [...(groups.get(key) ?? []), t]);
  }
  return [...groups.values()].map((rows) => {
    const sac = rows[0].sacHsnCode;
    const head = sac
      ? `<tr><td colspan="2" class="sub">SAC/HSN ${esc(sac)} · taxable ${money(rows[0].taxableAmount)}</td></tr>`
      : '';
    return head + rows.map((t) => sac
      ? `<tr><td>${esc(t.componentCode)} ${t.taxRate}%</td><td class="r">${money(t.taxAmount)}</td></tr>`
      : `<tr><td>${esc(t.componentCode)} ${t.taxRate}% on ${money(t.taxableAmount)}</td><td class="r">${money(t.taxAmount)}</td></tr>`).join('');
  }).join('');
}

export function billPrintHtml(p: DineBillPrint): string {
  const b = p.bill;
  // A tax-inclusive bill shows prices as charged; its tax is contained in them (DineOps tax T8).
  const inclusive = !!b.pricesIncludeTax;
  const when = new Date(b.generatedAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
  // A complimentary line (DineOps tax T14) prints at its menu value, marked; its value comes off as
  // "Complimentary", apart from the bill's discount.
  const lines = b.lines.map((l) => `
    <tr>
      <td class="l">${esc(l.itemName)}${l.variantName ? ` (${esc(l.variantName)})` : ''}${l.addons.length ? `<div class="sub">+ ${esc(l.addons.join(', '))}</div>` : ''}${l.isComplimentary ? `<div class="sub">Complimentary${l.compReason ? ` — ${esc(l.compReason)}` : ''}</div>` : ''}</td>
      <td class="r">${l.quantity}</td>
      <td class="r">${money(l.unitPrice)}</td>
      <td class="r">${money(l.isComplimentary ? l.unitPrice * l.quantity : inclusive ? l.grossAmount ?? l.lineTotal : l.lineTotal)}</td>
    </tr>`).join('');
  const taxes = taxSummaryRows(b.taxes);
  const containedTax = b.taxInclusiveAmount ?? 0;
  const chargedSubtotal = b.chargedSubtotal ?? b.subtotal;
  const comp = Math.round(b.lines.filter((l) => l.isComplimentary).reduce((s, l) => s + l.unitPrice * l.quantity, 0) * 100) / 100;
  // On an exclusive bill a complimentary line's discount is its whole price, net and charged alike.
  const chargedDiscount = Math.max(0, Math.round(((inclusive
    ? chargedSubtotal - b.lines.reduce((s, l) => s + (l.grossAmount ?? l.lineTotal), 0)
    : b.discountAmount) - comp) * 100) / 100);
  const compRow = comp > 0 ? `<tr><td>Complimentary${b.complimentary?.department ? ` (${esc(b.complimentary.department)})` : ''}</td><td class="r">-${money(comp)}</td></tr>` : '';
  const addedTax = Math.round((b.taxAmount - containedTax) * 100) / 100;
  const pays = b.payments.filter((x) => x.paymentStatus === 'SUCCESS').map((x) =>
    `<tr><td>${esc(x.paymentMethod)}${x.referenceNo ? ` ${esc(x.referenceNo)}` : ''}</td><td class="r">${money(x.amount)}</td></tr>`).join('')
    // Spec 14.1: a room-charged bill prints as "Charged to room 302".
    + (b.roomCharge?.postingStatus === 'POSTED'
      ? `<tr><td>Charged to room ${esc(b.roomCharge.room ?? '')}${b.roomCharge.guestName ? `<div class="sub">${esc(b.roomCharge.guestName)}</div>` : ''}</td><td class="r">${money(b.roomCharge.amount)}</td></tr>`
      : '');

  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(b.billNumber)}</title>
<style>
  @page { size: 80mm auto; margin: 4mm; }
  body { font: 12px/1.35 "Courier New", monospace; width: 72mm; margin: 0 auto; color: #000; }
  h1 { font-size: 15px; text-align: center; margin: 0; }
  .c { text-align: center; } .r { text-align: right; } .l { text-align: left; }
  .sub { font-size: 10px; color: #333; }
  .rule { border-top: 1px dashed #000; margin: 6px 0; }
  table { width: 100%; border-collapse: collapse; } td, th { padding: 1px 0; vertical-align: top; }
  th { border-bottom: 1px solid #000; font-weight: 700; }
  .grand td { font-weight: 700; font-size: 13px; border-top: 1px solid #000; padding-top: 3px; }
  .void { text-align: center; font-weight: 700; font-size: 16px; border: 2px solid #000; padding: 4px; margin: 6px 0; }
  .foot { font-size: 10px; text-align: center; margin-top: 8px; }
</style></head><body>
  <h1>${esc(p.outletName)}</h1>
  ${p.address ? `<div class="c">${esc(p.address)}</div>` : ''}
  ${p.gstin ? `<div class="c">GSTIN ${esc(p.gstin)}</div>` : ''}
  ${p.fssai ? `<div class="c">FSSAI ${esc(p.fssai)}</div>` : ''}
  ${p.billingPointName ? `<div class="c sub">Counter: ${esc(p.billingPointName)}</div>` : ''}
  <div class="rule"></div>
  <div class="c"><b>${esc(p.title)}</b></div>
  ${b.billStatus === 'VOID' ? `<div class="void">VOID${b.voidReason ? ` — ${esc(b.voidReason)}` : ''}</div>` : ''}
  ${b.complimentary && b.billStatus !== 'VOID' ? `<div class="void">COMPLIMENTARY<div class="sub">${esc(b.complimentary.reason)}</div></div>` : ''}
  <table>
    <tr><td>Invoice</td><td class="r"><b>${esc(b.billNumber)}</b></td></tr>
    <tr><td>Date</td><td class="r">${esc(when)}</td></tr>
    <tr><td>Order</td><td class="r">${esc(b.orderNumber)}</td></tr>
    <tr><td>${b.tableNumber ? 'Table' : b.roomNumber ? 'Room' : 'Takeaway'}</td><td class="r">${esc(b.tableNumber ?? b.roomNumber ?? '')} · ${b.covers} cover${b.covers === 1 ? '' : 's'}</td></tr>
    <tr><td>Customer</td><td class="r">${esc(customerLabel(b.customerType))}${b.customerName ? `<br>${esc(b.customerName)}` : ''}${b.customerType === 'IN_HOUSE' && b.customerRoom ? `<br>Room ${esc(b.customerRoom)}` : ''}${b.customerPhone ? `<br>${esc(b.customerPhone)}` : ''}${b.customerGstin ? `<br>GSTIN ${esc(b.customerGstin)}` : ''}</td></tr>
  </table>
  <div class="rule"></div>
  <table>
    <thead><tr><th class="l">Item</th><th class="r">Qty</th><th class="r">Rate</th><th class="r">Amount</th></tr></thead>
    <tbody>${lines}</tbody>
  </table>
  <div class="rule"></div>
  <table>
    ${inclusive
      ? `<tr><td>Items (tax included)</td><td class="r">${money(chargedSubtotal)}</td></tr>
    ${compRow}
    ${chargedDiscount > 0 ? `<tr><td>Discount${b.discount?.reason ? ` (${esc(b.discount.reason)})` : ''}</td><td class="r">-${money(chargedDiscount)}</td></tr>` : ''}
    ${b.serviceChargeAmt > 0 ? `<tr><td>Service charge ${b.serviceChargePct}%</td><td class="r">${money(b.serviceChargeAmt)}</td></tr>` : ''}
    ${addedTax > 0 ? `<tr><td>GST on service charge</td><td class="r">${money(addedTax)}</td></tr>` : ''}`
      : `<tr><td>Subtotal</td><td class="r">${money(b.subtotal)}</td></tr>
    ${compRow}
    ${chargedDiscount > 0 ? `<tr><td>Discount${b.discount?.reason ? ` (${esc(b.discount.reason)})` : ''}</td><td class="r">-${money(chargedDiscount)}</td></tr>` : ''}
    ${b.serviceChargeAmt > 0 ? `<tr><td>Service charge ${b.serviceChargePct}%</td><td class="r">${money(b.serviceChargeAmt)}</td></tr>` : ''}
    ${taxes}`}
    ${b.roundOffAmount !== 0 ? `<tr><td>Round-off</td><td class="r">${b.roundOffAmount > 0 ? '' : '-'}${money(Math.abs(b.roundOffAmount))}</td></tr>` : ''}
    <tr class="grand"><td>TOTAL</td><td class="r">${money(b.totalAmount)}</td></tr>
  </table>
  ${inclusive && taxes ? `<div class="rule"></div><div class="sub">Tax summary (included above)</div><table>${taxes}</table>` : ''}
  <div class="sub c">${esc(p.amountInWords)}</div>
  ${pays ? `<div class="rule"></div><table>${pays}${b.balanceDue > 0 ? `<tr><td>Balance due</td><td class="r">${money(b.balanceDue)}</td></tr>` : ''}</table>` : ''}
  <div class="rule"></div>
  <div class="foot">Thank you. ${inclusive
    ? `Prices include GST${containedTax > 0 ? ` of ${money(containedTax)}` : ''}, as summarised above.`
    : 'GST is charged as itemised above.'}</div>
</body></html>`;
}

/** Opens the invoice in a new window and triggers print. Returns false when the browser blocked the popup. */
export function printBill(p: DineBillPrint): boolean {
  const w = window.open('', '_blank', 'width=420,height=720');
  if (!w) return false;
  w.document.open();
  w.document.write(billPrintHtml(p));
  w.document.close();
  w.focus();
  setTimeout(() => { w.print(); }, 250);
  return true;
}
